import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { INTENT_OPTIONS } from '../lib/pillars';

const QUESTIONS = [
  // CONNECTION
  {
    id: 'connection_presence',
    driver: 'connection',
    type: 'presence',
    text: 'When emotional connection is present, how does the experience feel?',
    options: [
      { label: 'Much deeper and more satisfying', score: 2 },
      { label: 'Better, but not required', score: 1 },
      { label: "Doesn't change much", score: 0 },
      { label: 'Sometimes makes it more complicated', score: -1 }
    ]
  },
  {
    id: 'connection_absence',
    driver: 'connection',
    type: 'absence',
    text: 'When emotional connection is missing, how does it feel?',
    options: [
      { label: 'Empty or disconnected', score: 2 },
      { label: "Enjoyable, but doesn't last", score: 1 },
      { label: 'Still fully satisfying', score: 0 },
      { label: 'Depends on other factors', score: -1 }
    ]
  },
  // DESIRE
  {
    id: 'desire_presence',
    driver: 'desire',
    type: 'presence',
    text: 'When you feel strongly desired, what happens to the experience?',
    options: [
      { label: 'It becomes much more intense', score: 2 },
      { label: "It helps, but isn't necessary", score: 1 },
      { label: "It doesn't change much", score: 0 },
      { label: 'It can feel overwhelming or performative', score: -1 }
    ]
  },
  {
    id: 'desire_absence',
    driver: 'desire',
    type: 'absence',
    text: "When that sense of desire isn't there, what happens?",
    options: [
      { label: 'The experience falls flat', score: 2 },
      { label: "It's still good, just less intense", score: 1 },
      { label: "It doesn't matter much", score: 0 },
      { label: "I don't really notice", score: -1 }
    ]
  },
  // NOVELTY
  {
    id: 'novelty_presence',
    driver: 'novelty',
    type: 'presence',
    text: 'When something feels new or unpredictable, how does it affect the experience?',
    options: [
      { label: 'It makes it much more exciting', score: 2 },
      { label: "It adds something, but isn't essential", score: 1 },
      { label: "It doesn't change much", score: 0 },
      { label: 'I prefer things familiar and consistent', score: -1 }
    ]
  },
  {
    id: 'novelty_absence',
    driver: 'novelty',
    type: 'absence',
    text: 'When things feel familiar or routine, what happens?',
    options: [
      { label: 'It becomes less engaging', score: 2 },
      { label: "It's still good, just calmer", score: 1 },
      { label: "It doesn't matter", score: 0 },
      { label: 'I actually prefer it', score: -1 }
    ]
  },
  // POWER
  {
    id: 'power_presence',
    driver: 'power',
    type: 'presence',
    text: 'When there\'s a clear dynamic (leading, following, tension), how does it affect the experience?',
    options: [
      { label: 'It makes it much more engaging', score: 2 },
      { label: "It helps, but isn't necessary", score: 1 },
      { label: "It doesn't change much", score: 0 },
      { label: 'I prefer things more equal or neutral', score: -1 }
    ]
  },
  {
    id: 'power_absence',
    driver: 'power',
    type: 'absence',
    text: "When that dynamic isn't present, what happens?",
    options: [
      { label: 'It feels flat or directionless', score: 2 },
      { label: "It's still good, just different", score: 1 },
      { label: "It doesn't matter", score: 0 },
      { label: 'I prefer it that way', score: -1 }
    ]
  },
  // PLAY
  {
    id: 'play_presence',
    driver: 'play',
    type: 'presence',
    text: 'When things feel playful or light, how does that affect the experience?',
    options: [
      { label: 'It makes it more enjoyable and natural', score: 2 },
      { label: "It helps, but isn't necessary", score: 1 },
      { label: "It doesn't change much", score: 0 },
      { label: 'I prefer things more serious or focused', score: -1 }
    ]
  },
  {
    id: 'play_absence',
    driver: 'play',
    type: 'absence',
    text: 'When playfulness is missing, what happens?',
    options: [
      { label: 'It feels too serious or pressured', score: 2 },
      { label: "It's still fine", score: 1 },
      { label: "It doesn't matter", score: 0 },
      { label: 'I prefer it that way', score: -1 }
    ]
  }
];

const SYNTHESIS = {
  id: 'synthesis',
  text: 'Think about your best experiences. What made them feel right?',
  options: [
    { label: 'The connection', driver: 'connection' },
    { label: 'The intensity / desire', driver: 'desire' },
    { label: 'The energy or tension', driver: 'power' },
    { label: 'The novelty', driver: 'novelty' },
    { label: 'The ease or playfulness', driver: 'play' }
  ]
};

const TOTAL_STEPS = QUESTIONS.length + 1; // 10 questions + 1 synthesis

// Convert raw driver scores (-2 to +6) to 0-10 importance scale
const normalizeScore = (raw) => {
  const clamped = Math.max(-2, Math.min(6, raw));
  return Math.round(((clamped + 2) / 8) * 10);
};

export const ExperienceQuestions = ({ onComplete, onBack, contextLabel, intentLabels }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [synthesisAnswers, setSynthesisAnswers] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentIndex]);

  const isSynthesis = currentIndex === QUESTIONS.length;
  const currentQ = isSynthesis ? SYNTHESIS : QUESTIONS[currentIndex];
  const currentAnswer = isSynthesis ? synthesisAnswers : answers[currentQ.id];
  const isAnswered = isSynthesis ? synthesisAnswers.length > 0 : (currentAnswer !== undefined && currentAnswer !== null);
  const isLast = currentIndex === TOTAL_STEPS - 1;

  // Build context bar text
  const intentText = (intentLabels || [])
    .map(v => INTENT_OPTIONS.find(o => o.value === v)?.label)
    .filter(Boolean)
    .join(' · ');
  const contextBarText = [contextLabel, intentText].filter(Boolean).join(' · ');

  const handleSelect = useCallback((value) => {
    if (isSynthesis) {
      setSynthesisAnswers(prev => {
        if (prev.includes(value)) return prev.filter(v => v !== value);
        if (prev.length >= 2) return [prev[1], value];
        return [...prev, value];
      });
    } else {
      setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
    }
  }, [isSynthesis, currentQ.id]);

  const handleNext = useCallback(() => {
    if (isLast) {
      // Compute driver scores
      const driverScores = { connection: 0, desire: 0, novelty: 0, power: 0, play: 0 };

      QUESTIONS.forEach(q => {
        const ans = answers[q.id];
        if (ans !== undefined) {
          driverScores[q.driver] += ans;
        }
      });

      // Synthesis boost: each selected option adds +1
      synthesisAnswers.forEach(driver => {
        if (driver && driverScores[driver] !== undefined) {
          driverScores[driver] += 1;
        }
      });

      // Normalize to importance 0-10 format
      const drivers = {};
      Object.keys(driverScores).forEach(d => {
        drivers[d] = { importance: normalizeScore(driverScores[d]) };
      });

      onComplete(drivers);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [isLast, answers, synthesisAnswers, onComplete]);

  const handleBack = useCallback(() => {
    if (currentIndex === 0) {
      onBack();
    } else {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex, onBack]);

  const progress = ((currentIndex + 1) / TOTAL_STEPS) * 100;

  return (
    <section className="min-h-screen py-24 px-6 pb-40" data-testid="experience-questions">
      <div className="max-w-xl mx-auto">
        {/* Progress */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-10"
        >
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Question {currentIndex + 1} of {TOTAL_STEPS}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-1 bg-border/40 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[hsl(var(--gold))] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        {/* Context bar */}
        {contextBarText && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8"
            data-testid="context-bar"
          >
            <div className="bg-muted/30 border border-border/40 rounded-lg px-4 py-2.5">
              <p className="text-xs text-foreground/70">
                <span className="text-muted-foreground">Context:</span> {contextBarText}
              </p>
              <p className="text-[11px] text-muted-foreground/60 mt-0.5">
                Answer based on this specific situation, not in general.
              </p>
            </div>
          </motion.div>
        )}

        {/* Question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQ.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          >
            <h2
              className="text-xl sm:text-2xl font-light text-foreground leading-snug mb-2"
              data-testid="experience-question-text"
            >
              {currentQ.text}
            </h2>

            {isSynthesis && (
              <p className="text-xs text-muted-foreground mb-6">Select up to 2</p>
            )}
            {!isSynthesis && <div className="mb-8" />}

            <div className="space-y-3">
              {currentQ.options.map((opt, i) => {
                const value = isSynthesis ? opt.driver : opt.score;
                const isSelected = isSynthesis
                  ? synthesisAnswers.includes(value)
                  : currentAnswer === value;

                return (
                  <Card
                    key={i}
                    onClick={() => handleSelect(value)}
                    className={`p-4 cursor-pointer transition-all border ${
                      isSelected
                        ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                        : 'border-border hover:border-[hsl(var(--teal)/0.3)]'
                    }`}
                    data-testid={`experience-option-${i}`}
                  >
                    <span className="text-sm text-foreground font-light">{opt.label}</span>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Fixed nav */}
      <div className="fixed bottom-14 sm:bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border/40 py-4 px-6 z-40">
        <div className="max-w-xl mx-auto flex justify-between">
          <Button
            onClick={handleBack}
            variant="ghost"
            className="rounded-full px-6"
            data-testid="experience-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isAnswered}
            className="rounded-full px-8"
            data-testid="experience-next-btn"
          >
            {isLast ? 'See Results' : 'Next'}
            {!isLast && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
    </section>
  );
};
